import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { apiPost } from "@/lib/shieldx";
import { StatusBadge } from "@/components/shieldx/Badges";
import { PasswordInput } from "@/components/shieldx/PasswordInput";
import { X, ShieldAlert, Loader2, Pencil, MoreVertical, Lock, CheckCircle2, AlertTriangle } from "lucide-react";

export const DANGEROUS = new Set(["SUSPEND", "REVOKE", "CHANGE_ROLE", "REVOKE_SESSIONS", "SETTINGS_UPDATE", "DEACTIVATE"]);

const SEC_TONE = {
  PROTECTED: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  MONITORING: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
  PROTECTION_DEGRADED: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  AT_RISK: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  VERIFICATION_REQUIRED: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  CONFIGURATION_REQUIRED: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
  UNKNOWN: "bg-slate-700/40 text-slate-300 border-slate-600/40",
  SUSPENDED: "bg-rose-500/15 text-rose-300 border-rose-500/40",
  REVOKED: "bg-red-600/20 text-red-300 border-red-600/40",
};

export function SecBadge({ value, testid }) {
  if (!value) return null;
  const key = String(value).toUpperCase();
  return (
    <span data-testid={testid}
      className={cn("inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 font-mono text-[11px] font-medium tracking-wide whitespace-nowrap",
        SEC_TONE[key] || "bg-slate-800/60 text-slate-300 border-slate-700/50")}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
      {key.replace(/_/g, " ")}
    </span>
  );
}

export function SAStat({ label, value, tone = "slate", icon: Icon, delay = 0, onClick, testid }) {
  const tones = {
    slate: "text-slate-50", blue: "text-blue-300", emerald: "text-emerald-400",
    amber: "text-amber-300", red: "text-red-400", cyan: "text-cyan-300", purple: "text-purple-300",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.35 }}
      onClick={onClick} data-testid={testid}
      className={cn("rounded-xl border border-slate-800 bg-slate-900/50 p-4 transition-colors",
        onClick && "cursor-pointer hover:border-blue-500/40 hover:bg-slate-900/80")}>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
        {Icon && <Icon className="h-4 w-4 text-slate-600" />}
      </div>
      <p className={cn("mt-1.5 font-display text-2xl font-bold tabular-nums", tones[tone])}>{value}</p>
    </motion.div>
  );
}

export function PencilBtn({ onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-700/60 bg-slate-800/50 text-slate-300 transition-colors hover:border-blue-500/50 hover:text-blue-300"
      title="Edit">
      <Pencil className="h-3.5 w-3.5" />
    </button>
  );
}

export function ActionMenu({ items, testid }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const h = () => setOpen(false);
    if (open) document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [open]);
  return (
    <div className="relative inline-block">
      <button data-testid={testid} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-700/60 bg-slate-800/50 text-slate-300 hover:border-slate-600 hover:text-white">
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 w-48 overflow-hidden rounded-lg border border-slate-800 bg-slate-900 py-1 shadow-2xl">
          {items.map((it) => (
            <button key={it.label} data-testid={it.testid}
              onClick={(e) => { e.stopPropagation(); setOpen(false); it.onClick(); }}
              className={cn("flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-800",
                it.danger ? "text-rose-300" : "text-slate-300")}>
              {it.icon && <it.icon className="h-3.5 w-3.5" />} {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* Unified action dialog: reason always required; password re-auth for dangerous actions. */
export function ActionDialog({ open, onClose, action, targetType, targetId, title, body, extraFields = [], onDone }) {
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [extra, setExtra] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const dangerous = DANGEROUS.has(action);

  useEffect(() => { if (open) { setReason(""); setPassword(""); setExtra({}); setError(""); } }, [open, action]);
  if (!open) return null;

  const submit = async () => {
    if (!reason.trim()) { setError("A reason is mandatory."); return; }
    if (dangerous && !password) { setError("Password re-authentication is required."); return; }
    setLoading(true); setError("");
    try {
      let step_up_token = null;
      if (dangerous) {
        const r = await apiPost("/super-admin/step-up", { password, action, target_type: targetType, target_id: targetId, reason });
        step_up_token = r.step_up_token;
      }
      await onDone({ reason, step_up_token, ...extra });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Action failed. Please retry.");
    } finally { setLoading(false); }
  };

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <motion.div initial={{ opacity: 0, scale: 0.96, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
          className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
          data-testid="action-dialog">
          <div className={cn("flex items-center gap-2 border-b border-slate-800 px-5 py-4",
            dangerous ? "bg-rose-500/5" : "bg-slate-900")}>
            {dangerous ? <ShieldAlert className="h-5 w-5 text-rose-400" /> : <CheckCircle2 className="h-5 w-5 text-blue-400" />}
            <h3 className="font-display text-sm font-semibold text-slate-100">{title}</h3>
          </div>
          <div className="space-y-3.5 p-5">
            {body && <p className="text-sm text-slate-400">{body}</p>}
            {extraFields.map((f) => (
              <div key={f.key}>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">{f.label}</label>
                {f.type === "select" ? (
                  <select data-testid={`action-field-${f.key}`} value={extra[f.key] || ""} onChange={(e) => setExtra({ ...extra, [f.key]: e.target.value })}
                    className="h-10 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 text-sm text-slate-200 outline-none focus:border-blue-500/50">
                    <option value="">Select…</option>
                    {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input data-testid={`action-field-${f.key}`} value={extra[f.key] || ""} onChange={(e) => setExtra({ ...extra, [f.key]: e.target.value })}
                    className="h-10 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 text-sm text-slate-200 outline-none focus:border-blue-500/50" />
                )}
              </div>
            ))}
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Reason (mandatory)</label>
              <textarea data-testid="action-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                placeholder="Document why this action is being performed…"
                className="w-full rounded-lg border border-slate-800 bg-slate-950 p-3 text-sm text-slate-200 outline-none focus:border-blue-500/50" />
            </div>
            {dangerous && (
              <div>
                <label className="mb-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-rose-300">
                  <Lock className="h-3 w-3" /> Password Re-Authentication
                </label>
                <PasswordInput data-testid="action-password" testid="action-password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Re-enter your Super Admin password" autoComplete="current-password"
                  className="h-10 w-full rounded-lg border border-rose-500/30 bg-slate-950 px-3 text-sm text-slate-200 outline-none focus:border-rose-500/60" />
                <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-500"><AlertTriangle className="h-3 w-3" /> Not MFA — real MFA is not wired. Verified server-side, never stored.</p>
              </div>
            )}
            {error && <p data-testid="action-error" className="text-sm text-rose-400">{error}</p>}
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-800 px-5 py-3.5">
            <button onClick={onClose} data-testid="action-cancel" className="rounded-lg px-4 py-2 text-sm text-slate-400 hover:text-slate-200">Cancel</button>
            <button onClick={submit} disabled={loading} data-testid="action-confirm"
              className={cn("inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60",
                dangerous ? "bg-rose-600 hover:bg-rose-500" : "bg-blue-600 hover:bg-blue-500")}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Confirm
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>, document.body);
}

export function EditDialog({ open, onClose, title, fields, initial, onSave }) {
  const [vals, setVals] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setVals(initial || {}); setError(""); } }, [open, initial]);
  if (!open) return null;
  const submit = async () => {
    setLoading(true); setError("");
    try { await onSave(vals); onClose(); }
    catch (e) { setError(e?.response?.data?.detail || "Save failed."); }
    finally { setLoading(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="relative max-h-[85vh] w-full max-w-lg overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl" data-testid="edit-dialog">
        <div className="flex items-center gap-2 border-b border-slate-800 px-5 py-4">
          <Pencil className="h-4 w-4 text-blue-400" />
          <h3 className="font-display text-sm font-semibold text-slate-100">{title}</h3>
        </div>
        <div className="max-h-[55vh] space-y-3.5 overflow-y-auto p-5">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">{f.label}</label>
              {f.type === "select" ? (
                <select data-testid={`edit-${f.key}`} value={vals[f.key] ?? ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })}
                  className="h-10 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 text-sm text-slate-200 outline-none focus:border-blue-500/50">
                  {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : f.type === "textarea" ? (
                <textarea data-testid={`edit-${f.key}`} rows={3} value={vals[f.key] ?? ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })}
                  className="w-full rounded-lg border border-slate-800 bg-slate-950 p-3 text-sm text-slate-200 outline-none focus:border-blue-500/50" />
              ) : (
                <input data-testid={`edit-${f.key}`} value={vals[f.key] ?? ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })}
                  className="h-10 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 text-sm text-slate-200 outline-none focus:border-blue-500/50" />
              )}
            </div>
          ))}
          {error && <p className="text-sm text-rose-400">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-800 px-5 py-3.5">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-slate-400 hover:text-slate-200">Cancel</button>
          <button onClick={submit} disabled={loading} data-testid="edit-save"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
          </button>
        </div>
      </motion.div>
    </div>, document.body);
}

export function Drawer({ open, onClose, title, subtitle, badge, tabs, loading, testid }) {
  const [active, setActive] = useState(tabs?.[0]?.key);
  useEffect(() => { if (open && tabs?.length) setActive(tabs[0].key); }, [open, tabs?.length]);
  if (!open) return null;
  const current = tabs?.find((t) => t.key === active);
  return createPortal(
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid={testid}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 240 }}
        className="relative flex h-full w-full max-w-3xl flex-col border-l border-slate-800 bg-slate-950 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 px-6 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <h2 className="truncate font-display text-lg font-bold text-slate-50">{title}</h2>
              {badge}
            </div>
            {subtitle && <p className="mt-0.5 truncate font-mono text-[11px] text-slate-500">{subtitle}</p>}
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="shrink-0 rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        {loading ? (
          <div className="flex flex-1 items-center justify-center text-slate-500"><Loader2 className="mr-2 h-5 w-5 animate-spin text-blue-500" /> Loading 360° view…</div>
        ) : (
          <>
            <div className="flex gap-1 overflow-x-auto border-b border-slate-800 px-4 py-2">
              {tabs?.map((t) => (
                <button key={t.key} onClick={() => setActive(t.key)} data-testid={`tab-${t.key}`}
                  className={cn("whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
                    active === t.key ? "bg-blue-600/15 text-blue-300 ring-1 ring-inset ring-blue-500/30" : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-200")}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <motion.div key={active} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
                {current?.content}
              </motion.div>
            </div>
          </>
        )}
      </motion.div>
    </div>, document.body);
}

export function Timeline({ events }) {
  const toneFor = (p) => ({
    Registration: "bg-blue-500", Verification: "bg-emerald-500", Assets: "bg-cyan-500",
    Incidents: "bg-orange-500", Simulations: "bg-purple-500", Policies: "bg-indigo-500",
    "Admin Action": "bg-rose-500", Recovery: "bg-emerald-400", Activity: "bg-slate-500",
  }[p] || "bg-slate-500");
  if (!events?.length) return <p className="text-sm text-slate-500">No timeline events yet.</p>;
  return (
    <div className="relative ml-2 space-y-4 border-l border-slate-800 pl-6" data-testid="sa-timeline">
      {events.map((e, i) => (
        <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }} className="relative">
          <span className={cn("absolute -left-[31px] top-1 h-3 w-3 rounded-full ring-4 ring-slate-950", toneFor(e.phase))} />
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{e.phase}</span>
            <span className="text-[11px] text-slate-600">{e.ts ? new Date(e.ts).toLocaleString() : ""}</span>
          </div>
          <p className="mt-0.5 text-sm font-medium text-slate-200">{e.label}</p>
          {e.detail && <p className="text-xs text-slate-500">{e.detail}</p>}
        </motion.div>
      ))}
    </div>
  );
}

export function FilterBar({ filters, values, onChange, onSearch, searchValue, searchPlaceholder = "Search…" }) {
  return (
    <div className="mb-5 flex flex-wrap items-center gap-2.5">
      <input data-testid="sa-search" value={searchValue} onChange={(e) => onSearch(e.target.value)} placeholder={searchPlaceholder}
        className="h-9 min-w-[220px] flex-1 rounded-lg border border-slate-800 bg-slate-900/70 px-3 text-sm text-slate-200 placeholder:text-slate-600 outline-none focus:border-blue-500/50" />
      {filters.map((f) => (
        <select key={f.key} data-testid={`filter-${f.key}`} value={values[f.key] || ""} onChange={(e) => onChange(f.key, e.target.value)}
          className="h-9 rounded-lg border border-slate-800 bg-slate-900/70 px-2.5 text-xs text-slate-300 outline-none focus:border-blue-500/50">
          <option value="">{f.label}</option>
          {f.options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
        </select>
      ))}
    </div>
  );
}

export function KV({ label, value, mono }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-slate-800/60 py-2">
      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <span className={cn("text-sm text-slate-200", mono && "font-mono text-xs")}>{value ?? "—"}</span>
    </div>
  );
}

export function Section({ title, children, right }) {
  return (
    <div className="mb-5">
      <div className="mb-2.5 flex items-center justify-between">
        <h4 className="font-display text-xs font-semibold uppercase tracking-widest text-slate-400">{title}</h4>
        {right}
      </div>
      {children}
    </div>
  );
}

export { StatusBadge };
